import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  Button
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_CLIENT_FIXED_ASSETS_LIST } from "../../constant/Apipath";
import { showErrorToast } from "../../toast/toast";
import assetPlaceholder from "../../assets/images/image-unavailable.jpg";
import { BIGrey, BIRed } from "../../assets/helpers";

// --- Helpers ---
const parseResponseList = (response) => {
  if (response?.status === 200) {
    const payload = response?.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.Data)) return payload.Data;
    if (Array.isArray(payload?.Result)) return payload.Result;
    if (Array.isArray(payload)) return payload;
  }
  return [];
};

const mapAsset = (item) => ({
  AssetId: item.id ?? item.assetId,
  AssetCode: item.assetCode ?? item.AssetCode ?? item.barCode,
  CategoryId: item.assetCategoryId ?? item.categoryId ?? item.AssetCategoryId ?? item.assetCategory ?? item.Category ?? 0,
  CategoryName: item.assetCategoryName ?? item.categoryName,
  SubCategoryId: item.assetSubCategoryId ?? item.subCategoryId ?? item.AssetSubCategoryId ?? item.assetSubCategory ?? item.SubCategory ?? 0,
  SubCategoryName: item.assetSubCategoryName ?? item.subCategoryName,
  SerialNo: item.serialNo ?? item.SerialNumber,
  AssignedToName: item.assignedToName ?? item.assignedTo ?? item.supervisorName ?? "Not Assigned",
  Location: item.location ?? item.currentLocation,
  LocationType: item.locationType,
  LocationTitle: item.locationTitle ?? item.LocationTitle ?? item.Location ?? item.Title,
  StatusTypeName: item.assetStatusName ?? item.statusName,
  AssetStatusId: item.assetStatusId,
  StatusId: item.statusId,
  StatusName: item.statusName ?? item.assetStatusName,
  AssetImageUrl: item.pictureList?.[0]?.fileUrl ?? item.pictureList?.[0]?.pictureUrl ?? item.imageUrl ?? item.assetImageUrl,
  BarCode: item?.barCode,
  PurchaseDate: item.purchasedDate,
  WarrantyExpireDate: item.warrantyExpireDate
});

const getAssetImage = (asset) => asset?.AssetImageUrl ?? asset?.ImageUrl ?? assetPlaceholder;

const getDisplayValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "NA";
};

const getStatusColors = (label) => {
  if (!label) return { bgcolor: "#f0f0f0", color: "#333" };
  const key = label.toLowerCase().trim();
  switch (key) {
    case "active": return { bgcolor: "#d7f8e4", color: "#1b5e20" };
    case "lost": return { bgcolor: "#fdecea", color: "#b71c1c" };
    case "out for repair": case "under repair": case "repair": return { bgcolor: "#fff4e5", color: "#f57c00" };
    case "inactive": case "disabled": return { bgcolor: "#f4f4f5", color: "#616161" };
    default: return { bgcolor: "#f0f0f0", color: "#333" };
  }
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    const trimmed = (value === undefined || value === null || typeof value === "object") ? "" : String(value).trim();
    if (trimmed) return trimmed;
  }
  return "";
};

const getLocationDisplay = (record) => firstNonEmpty(record.LocationTitle, record.locationTitle) || "NA";

const FILTER_LABELS = {
  location: "Location",
  job: "Job",
  employee: "Employee",
  category: "Category",
  subCategory: "Sub Category",
  status: "Status"
};
const PAGE_SIZE = 10;

const FixedAssetList = ({
  onSelectAsset,
  appliedFilters,
  onOpenFilter,
  onClearFilter,
  onClearAllFilters,
  restrictToOwnAssets = true
}) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main);
  
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNr, setPageNr] = useState(1);
  const listScrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageNrRef = useRef(1);

  const loadAssets = useCallback(async (filters, page = 1, append = false) => {
    const clientId = selectedClientData?.ClientId;
    const baseSearchObj = { clientId };

    if (filters.categoryId) {
      baseSearchObj.assetCategoryId = Number(filters.categoryId);
    }
    if (filters.subCategoryId) {
      baseSearchObj.assetSubCategoryId = Number(filters.subCategoryId);
    }
    if (filters.statusId) {
      baseSearchObj.assetStatusId = Number(filters.statusId);
    }

    const searchList = [baseSearchObj];
    const shouldRestrictToSelf = restrictToOwnAssets && selectedClientData?.ClientEmployeeId;
    if (shouldRestrictToSelf) {
      searchList.push({ locationType: "employee", location: selectedClientData?.ClientEmployeeId });
    }

    // Apply location / job / employee filters
    if (filters.job) {
      searchList.push({ locationType: "job", location: filters.jobId || filters.job });
    } else if (filters.employee) {
      searchList.push({ locationType: "employee", location: filters.employeeId || filters.employee });
    } else if (filters.location) {
      searchList.push({ locationType: "location", location: filters.location });
    }

    if (page === 1) setIsLoading(true);
    
    try {
      const res = await Postrequestcall(FETCH_CLIENT_FIXED_ASSETS_LIST, {
        NrOfRecPerPage: PAGE_SIZE,
        PageNr: page,
        FullSearch: "",
        FetchAllowedRecordsOnly: false,
        SearchList: searchList,
        SortList: [{ FieldName: "id", Direction: "Desc" }],
        TypeOfObjectReturned: "",
        IncludeRecordNr: true,
        UserId: loginData?.userInformation?.UserId
      }, loginData?.accessToken);

      if (res?.status === 200) {
        const list = parseResponseList(res).map(mapAsset);
        setAssets(prev => append ? [...prev, ...list] : list);
        setHasMore(list.length >= PAGE_SIZE);
      } else {
        throw new Error("Failed to fetch assets");
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      if (page === 1) setIsLoading(false);
      setLoadingMore(false);
    }
  }, [selectedClientData, loginData, restrictToOwnAssets]);

  useEffect(() => {
    loadAssets(appliedFilters, 1, false);
    setPageNr(1);
    pageNrRef.current = 1;
  }, [appliedFilters, loadAssets]);

  const loadNextPage = useCallback(() => {
    if (isLoading || loadingMore || !hasMore) return;

    const nextPage = pageNrRef.current + 1;
    pageNrRef.current = nextPage;
    setPageNr(nextPage);
    setLoadingMore(true);
    loadAssets(appliedFilters, nextPage, true);
  }, [isLoading, loadingMore, hasMore, appliedFilters, loadAssets]);

  useEffect(() => {
    const root = listScrollContainerRef.current;
    const target = sentinelRef.current;

    if (!root || !target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      {
        root,
        rootMargin: "0px 0px 300px 0px",
        threshold: 0,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadNextPage, assets.length]);

  const activeFilters = Object.entries(appliedFilters)
    .filter(([k, v]) => v && !k.endsWith("Id"))
    .map(([k, v]) => ({ key: k, value: v }));

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Filter Bar */}
      <Stack sx={{ mb: 2, alignItems: "flex-start" }} direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
        <Button variant="contained" startIcon={<FilterListIcon />} onClick={onOpenFilter}
          sx={{ textTransform: "none", borderRadius: 999, px: 2.5, backgroundColor: selectedClientData?.AccentColor, color: "#fff", boxShadow: "none", ":hover": { backgroundColor: selectedClientData?.AccentColor } }}>
          Search By
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ alignItems: "center" }}>
          {activeFilters.map((filter) => (
            <Chip 
              key={filter.key} 
              label={`${FILTER_LABELS[filter.key] || filter.key}: ${filter.value}`}
              onDelete={() => onClearFilter(filter.key)} 
              sx={{ borderRadius: 6, color: BIRed, borderColor: BIRed }} 
            />
          ))}
          {activeFilters.length > 0 && (
            <Button variant="text" onClick={onClearAllFilters} sx={{ textTransform: "none", color: BIGrey }}>
              Clear All
            </Button>
          )}
        </Stack>
      </Stack>

      {/* List Area */}
      <Box ref={listScrollContainerRef} sx={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : assets.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, color: "text.secondary" }}>
            <InventoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.6 }} />
            <Typography variant="h6">No Fixed Assets</Typography>
            <Typography variant="body2">There are currently no fixed assets available.</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {assets.map((asset, index) => {
              const locationDisplay = getLocationDisplay(asset);
              const statusLabel = asset?.StatusTypeName || asset?.StatusName;
              const statusStyle = getStatusColors(statusLabel);

              return (
                <Card key={`${asset.AssetId}-${index}`} sx={{ borderRadius: 3, display: "flex", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", backgroundColor: "#fff" }}
                  onClick={() => onSelectAsset(asset)}>
                  <CardMedia component="img" image={getAssetImage(asset)} alt={asset?.AssetName}
                    sx={{ width: { xs: 80, md: 160 }, maxHeight: { md: 140 }, objectFit: "cover", borderRadius: 2, margin: 2 }} />
                  <CardContent sx={{ flex: 1, padding: "16px 20px" }}>
                    <Stack spacing={1}>
                      <Stack sx={{ justifyContent: "space-between", alignItems: "flex-start" }} direction="row">
                        <Box>
                          <Typography sx={{ fontSize: { xs: "12px", md: "16px" }, fontWeight: 600 }} variant="h6">
                            {asset?.AssetName || "Asset"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {getDisplayValue(asset?.CategoryName)}
                            {asset?.SubCategoryName ? ` - ${asset?.SubCategoryName}` : ""}
                          </Typography>
                        </Box>
                        {
                          statusLabel && (
                            <Chip size="small" sx={{ borderRadius: 999, bgcolor: statusStyle.bgcolor, color: statusStyle.color, fontWeight: 600, fontSize: { xs: "10px", md: "14px" } }} label={statusLabel} />
                          )
                        }
                      </Stack>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Asset Code: {getDisplayValue(asset?.AssetCode, asset?.Code)}</Typography>
                      <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                        <LocationOnOutlinedIcon fontSize="small" sx={{ color: BIGrey }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>Asset Location : {locationDisplay}</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            {loadingMore && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>}
            <Box ref={sentinelRef} sx={{ height: 1 }} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default FixedAssetList;
